import React from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormHelperText,
  Chip,
  Box
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const mineralTypes = [
  'Gold',
  'Silver',
  'Copper',
  'Iron',
  'Zinc',
  'Lead',
  'Nickel',
  'Cobalt',
  'Lithium',
  'Rare Earth Elements',
  'Diamonds',
  'Other'
];

const traderTypes = [
  'Broker',
  'Dealer',
  'Distributor',
  'Exporter',
  'Importer',
  'Wholesaler',
  'Other'
];

const TraderRegistrationForm = ({ onSubmit, loading }) => {
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      role: '',
      mineralTypes: [],
      traderType: '',
      tradingVolume: '',
      marketRegions: ''
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      password: Yup.string()
        .min(8, 'Must be at least 8 characters')
        .required('Required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Required'),
      companyName: Yup.string().required('Required'),
      role: Yup.string().required('Required'),
      mineralTypes: Yup.array().min(1, 'Select at least one mineral type'),
      traderType: Yup.string().required('Required')
    }),
    onSubmit: (values) => {
      // Add stakeholder type
      onSubmit({ ...values, stakeholderType: 'trader' });
    }
  });

  return (
    <form onSubmit={formik.handleSubmit} className="p-4">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            label="First Name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            label="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="companyName"
            name="companyName"
            label="Company Name"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            helperText={formik.touched.companyName && formik.errors.companyName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="role"
            name="role"
            label="Your Role in the Company"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role && formik.errors.role}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            error={formik.touched.mineralTypes && Boolean(formik.errors.mineralTypes)}
          >
            <InputLabel id="mineralTypes-label">Minerals Traded</InputLabel>
            <Select
              labelId="mineralTypes-label"
              id="mineralTypes"
              name="mineralTypes"
              multiple
              value={formik.values.mineralTypes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {mineralTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.mineralTypes && formik.errors.mineralTypes && (
              <FormHelperText>{formik.errors.mineralTypes}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            error={formik.touched.traderType && Boolean(formik.errors.traderType)}
          >
            <InputLabel id="traderType-label">Trader Type</InputLabel>
            <Select
              labelId="traderType-label"
              id="traderType"
              name="traderType"
              value={formik.values.traderType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {traderTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.traderType && formik.errors.traderType && (
              <FormHelperText>{formik.errors.traderType}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="tradingVolume"
            name="tradingVolume"
            label="Estimated Annual Trading Volume (tons)"
            value={formik.values.tradingVolume}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.tradingVolume && Boolean(formik.errors.tradingVolume)}
            helperText={formik.touched.tradingVolume && formik.errors.tradingVolume}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="marketRegions"
            name="marketRegions"
            label="Market Regions (comma separated)"
            value={formik.values.marketRegions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.marketRegions && Boolean(formik.errors.marketRegions)}
            helperText={formik.touched.marketRegions && formik.errors.marketRegions}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            className="gradient-bg"
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? 'Registering...' : 'Register as Trader'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default TraderRegistrationForm;
